import { DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Injector,
  input,
  LOCALE_ID,
  runInInjectionContext,
  signal,
} from '@angular/core';
import { barSeries, chart, ChartComponent, xAxis, yAxis } from '@softapps/core/chart';
import {
  ButtonComponent,
  ButtonGroupComponent,
  Translation,
  TranslatePipe,
} from '@softline/ui-core';
import { ChartCard } from '../../functions/chart-mapping';

/** `value2` is always present here: a bar series' `yKey` may not be optional. */
interface ChartSeriesRow {
  label: string;
  value: number;
  value2: number;
}

/** Renders one tool result either as a bar chart or as the plain table behind it. */
@Component({
  selector: 'soft-ai-agent-chart-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ChartComponent,
    ButtonComponent,
    ButtonGroupComponent,
    TranslatePipe,
    DecimalPipe,
  ],
  templateUrl: './chart-card.component.html',
  styleUrls: ['./chart-card.component.css'],
})
export class AiAgentChartCardComponent {
  private readonly injector = inject(Injector);
  private readonly locale = inject(LOCALE_ID);

  readonly card = input.required<ChartCard>();

  protected readonly view = signal<'chart' | 'table'>('chart');

  protected readonly rows = computed(() => this.card().rows);

  /** Revenue runs into the millions — spelled out, the axis labels collide. */
  private readonly compact = new Intl.NumberFormat(this.locale, {
    notation: 'compact',
    maximumFractionDigits: 1,
  });

  private readonly seriesRows = computed<ChartSeriesRow[]>(() =>
    this.card().rows.map((row) => ({
      label: shorten(row.label),
      value: row.value,
      value2: row.value2 ?? 0,
    }))
  );

  // `chart()` calls `formatter()`, so it needs an injection context, and its
  // factory is a snapshot — the whole config is rebuilt when the card changes.
  protected readonly config = computed(() =>
    runInInjectionContext(this.injector, () => {
      const card = this.card();
      return chart<ChartSeriesRow>(
        this.seriesRows,
        (ref) => {
          // Upright only. `direction: 'horizontal'` moves the value key onto
          // the category axis, which renders "Keine Daten" with the amounts as
          // categories — long names are shortened instead (see `seriesRows`).
          xAxis(ref, { type: 'category' });
          yAxis(ref, {
            type: 'amount',
            labelFormatter: (value) => this.compact.format(value),
          });
          barSeries(ref, {
            label: signal(card.seriesLabel as Translation),
            xKey: 'label',
            yKey: 'value',
            direction: 'vertical',
          });
          if (card.kind === 'compare')
            barSeries(ref, {
              label: signal((card.series2Label ?? '') as Translation),
              xKey: 'label',
              yKey: 'value2',
              direction: 'vertical',
            });
        },
        { legend: card.kind === 'compare' }
      );
    })
  );
}

/** Axis labels are customer and product names; the table view keeps them whole. */
const MAX_LABEL_LENGTH = 18;

function shorten(label: string): string {
  return label.length <= MAX_LABEL_LENGTH
    ? label
    : `${label.slice(0, MAX_LABEL_LENGTH - 1).trimEnd()}…`;
}
